import { useState, useEffect } from "react";
import axios from "axios";
import { server } from "../server";
import Tooltip from "rc-tooltip";
import "rc-tooltip/assets/bootstrap_white.css";
import { toast } from "react-toastify";

const BusSeatView = ({ vehicleNumber }) => {
  const [seatData, setSeatData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBusDetails = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          throw new Error("User is not authenticated.");
        }

        const response = await axios.post(
          `${server}/busInfoUsingVehicleNumber`,
          { vehicleNumber },
          {
            headers: {
              Authorization: token,
              "Content-Type": "application/json",
            },
          }
        );

        const seatInfo = response.data?.["body-json"]?.body;
        if (seatInfo && seatInfo.seatAllocation && seatInfo.numberOfSeats) {
          setSeatData(seatInfo);
        } else {
          toast.error("Bus Details Not Found", { position: "top-right" });
          setSeatData(null);
        }
      } catch (err) {
        console.error(
          "Error fetching bus details:",
          err.response || err.message || err
        );
        setError(
          err.response?.data?.message ||
            err.message ||
            "Failed to fetch bus details."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchBusDetails();
  }, [vehicleNumber]);

  if (loading) return <p className="mt-4">Loading Bus View ...</p>;
  if (error) return <p className="text-danger mt-4">{error}</p>;
  if (!seatData) return null;

  const { numberOfSeats, seatAllocation } = seatData;
  const { girls, staff, boys } = seatAllocation;

  let seatCounter = 1;
  let seats = [];

  const generateSeats = (category, total, assigned) => {
    return Array.from({ length: total }, (_, index) => ({
      seatNumber: seatCounter++,
      type: category,
      assigned: index < assigned ? "assigned" : "available",
    }));
  };

  seats = [
    ...generateSeats("girls", girls.total, girls.assigned),
    ...generateSeats("staff", staff.total, staff.assigned),
    ...generateSeats("boys", boys.total, boys.assigned),
  ];

  while (seats.length < numberOfSeats) {
    seats.push({ seatNumber: seatCounter++, type: "empty" });
  }
  if (seats.length > numberOfSeats) {
    seats = seats.slice(0, numberOfSeats);
  }

  const reorderSeats = (seats) => {
    let reorderedSeats = [...seats];

    for (let i = 0; i < seats.length; i += 4) {
      if (i + 2 < seats.length) {
        [reorderedSeats[i + 1], reorderedSeats[i + 2]] = [
          reorderedSeats[i + 2],
          reorderedSeats[i + 1],
        ];
      }
    }

    return reorderedSeats;
  };

  const orderedSeats = reorderSeats(seats);

  const generateRows = () => {
    let totalSeats = Math.min(orderedSeats.length, numberOfSeats);
    let rows = Array(2)
      .fill(null)
      .map(() =>
        Array(2)
          .fill(null)
          .map(() => [])
      );

    let seatIndex = 0;

    for (let i = 0; i < Math.ceil(totalSeats / 2); i++) {
      for (let j = 0; j < 2; j++) {
        if (seatIndex < totalSeats) {
          rows[j][i % 2].push(orderedSeats[seatIndex++]);
        }
      }
    }

    return rows;
  };

  const sections = generateRows();

  return (
    <>
      <div className="mt-5 lh-sm">
        <span class="text-secondary">Total No. Of Seats</span> &mdash;{" "}
        {numberOfSeats}/{seatData.allocatedSeats.length}
        <br />
        <span class="text-secondary">Total Girls Seat</span> &mdash;{" "}
        {seatData.seatAllocation.girls.total}/
        {seatData.seatAllocation.girls.assigned}
        <br />
        <span class="text-secondary">Total Staff Seat</span> &mdash;{" "}
        {seatData.seatAllocation.staff.total}/
        {seatData.seatAllocation.staff.assigned}
        <br />
        <span class="text-secondary">Total Boys Seat</span> &mdash;{" "}
        {seatData.seatAllocation.boys.total}/
        {seatData.seatAllocation.boys.assigned}
      </div>

      <div className="mt-4 mb-3 d-flex align-items-center">
        <div className="d-flex bus-layout p-4 rounded-5 w-fit-content">
          <div className="d-flex flex-column justify-content-between align-items-end">
            <div className="d-flex">
              <img
                src="/assets/images/driver.svg"
                className="me-1 seat-m driver-icon"
                width="26"
                height="26"
              />
              <Tooltip
                placement="top"
                trigger={["hover"]}
                overlay={<span className="text-capitalize lh-1">Driver</span>}
              >
                <div className="position-relative d-flex justify-content-center align-items-center me-2">
                  <img
                    src="/assets/images/booked.svg"
                    className="seat-m"
                    width="26"
                    height="26"
                  />
                </div>
              </Tooltip>
            </div>

            <Tooltip
              placement="top"
              trigger={["hover"]}
              overlay={<span className="text-capitalize lh-1">Co-Driver</span>}
            >
              <div className="position-relative d-flex justify-content-center align-items-center me-2">
                <img
                  src="/assets/images/booked.svg"
                  className="seat-m"
                  width="26"
                  height="26"
                />
                <div className="position-absolute">
                  <p className="text-dark z-1 m-0 ms-n1 fs-8"></p>
                </div>
              </div>
            </Tooltip>
          </div>

          <div>
            {sections.map((rows, sectionIndex) => (
              <div
                key={sectionIndex}
                className={sectionIndex === 1 ? "mt-5" : ""}
              >
                {rows.map((row, rowIndex) => (
                  <div key={rowIndex} className="d-flex gap-1">
                    {row.map((seat, index) => {
                      let seatImg = "/assets/images/bus/empty.svg";

                      if (seat.type === "girls") {
                        seatImg =
                          seat.assigned === "assigned"
                            ? "/assets/images/bus/girls-assigned.svg"
                            : "/assets/images/bus/girls.svg";
                      } else if (seat.type === "staff") {
                        seatImg =
                          seat.assigned === "assigned"
                            ? "/assets/images/bus/staff-assigned.svg"
                            : "/assets/images/bus/staff.svg";
                      } else if (seat.type === "boys") {
                        seatImg =
                          seat.assigned === "assigned"
                            ? "/assets/images/bus/boys-assigned.svg"
                            : "/assets/images/bus/boys.svg";
                      }

                      return (
                        <Tooltip
                          placement="top"
                          trigger={["hover"]}
                          overlay={
                            <span className="text-capitalize lh-1">
                              {seat.seatNumber}, {seat.type} &mdash;{" "}
                              {seat.assigned}
                              <br />
                            </span>
                          }
                        >
                          <div className="position-relative d-flex justify-content-center align-items-center">
                            <img
                              key={index}
                              src={seatImg}
                              className="seat-m"
                              width="26"
                              height="26"
                            />
                          </div>
                        </Tooltip>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="ps-5">
          <ul class="list-unstyled mb-0 d-flex flex-column g-0">
            <li class="d-flex flex-row gap-2">
              <span>
                <i
                  class="bi bi-check-circle-fill"
                  style={{ color: "#3c6e4e" }}
                ></i>
              </span>
              <span>Girls</span>
            </li>
            <li class="d-flex flex-row gap-2">
              <span>
                <i
                  class="bi bi-check-circle-fill"
                  style={{ color: "#9e3a3a" }}
                ></i>
              </span>
              <span>Staff</span>
            </li>
            <li class="d-flex flex-row gap-2">
              <span>
                <i
                  class="bi bi-check-circle-fill"
                  style={{ color: "#2f4f7f" }}
                ></i>
              </span>
              <span>Boys</span>
            </li>
            <li class="d-flex flex-row gap-2">
              <span>
                <i
                  class="bi bi-check-circle-fill"
                  style={{ color: "#999595" }}
                ></i>
              </span>
              <span>Driver</span>
            </li>
          </ul>
        </div>
      </div>

      <p class="text-secondary lh-1 text-danger">
        <i className="bi bi-info-circle-fill me-1"></i>
        Seat layout is for reference only, actual positions may vary.
        <br />
        Displays seat numbers, available and assigned.
      </p>
    </>
  );
};

export default BusSeatView;
